import { IMPORT_MODE, importModeLabels } from "@/constant/Imports/importMode";
import { useCallback, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { usePost } from "@/apiHooks/HttpFunctions";
import Button from "@/components/Button";
import Input from "@/components/Input/Input";
import ModalSkeleton from "@/components/ModalSkeleton";
import RenderToast from "@/components/RenderToast";
import Spinner from "@/components/Spinner";
import Table from "@/components/Table";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import BasicImportInitialValues from "@/data/basic-import-initial-values";
import classes from "@/Modals/BaseModal.module.css";
import { BasicImportSchema } from "@/schema/BasicImportSchema";
import { useFormik } from "formik";
import ScrollContainer from "react-indiana-drag-scroll";

export default function BaseImport({ dataName, setShow, show, api, refetch }) {
  const [failedRowsTableData, setFailedRowsTableData] = useState([]);

  const showError = (errorList, toastMessage = "Request failed.") => {
    setFailedRowsTableData(errorList);
    RenderToast({
      type: "error",
      message: toastMessage,
    });
  };

  const {
    mutate: save,
    error: saveError,
    isPending: saveIsPending,
  } = usePost({
    apiURL: `${api}/import`,
    isFormData: true,
    onSuccess: () => {
      closeModal();
      refetch();
      RenderToast({
        type: "success",
        message: `${dataName} imported successfully.`,
      });
    },
    onError: (error) => {
      const errorData = error.response?.data?.errors || [];
      const errorList = errorData?.map((error) => ({
        row: error.row,
        error: error.error,
      }));
      showError(errorList, "Import failed.");
    },
  });

  const importModeLabelsMap = importModeLabels();
  const modeRadioOptions = [
    {
      value: IMPORT_MODE.ADD_OVERWRITE,
      label: importModeLabelsMap[IMPORT_MODE.ADD_OVERWRITE],
    },
    {
      value: IMPORT_MODE.ADD_ERASE_ALL_EXISTING,
      label: importModeLabelsMap[IMPORT_MODE.ADD_ERASE_ALL_EXISTING],
    },
  ];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: BasicImportInitialValues(IMPORT_MODE.ADD_OVERWRITE),
    validationSchema: BasicImportSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const tableHeadings = useMemo(
    () => [
      {
        id: "row",
        label: "Row Number",
        style: { borderTop: 0, width: "100%" },
      },
      {
        id: "error",
        label: "Error",
        style: { borderTop: 0, width: "100%" },
        bodyStyle: { overflow: "visible", display: "block" },
      },
    ],
    []
  );

  const handleSubmit = async (values) => {
    setFailedRowsTableData([]);

    const formData = new FormData();
    formData.append("data_file", values.data_file);
    formData.append("mode", values.mode);
    formData.append("_method", "PATCH");

    save(formData);
  };

  const closeModal = useCallback(() => {
    formik.resetForm();
    setFailedRowsTableData([]);
    setShow(false);
  }, [formik, setShow]);

  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <div>
      <ModalSkeleton
        header={`Import ${dataName}`}
        onHide={closeModal}
        show={show}
        modalStyles={classes.modal}
        showCloseIcon={true}
      >
        <form onSubmit={formik.handleSubmit} className={classes.formDiv}>
          <Row>
            <Col md={12}>
              <Input
                type="file"
                labelClass={classes.label}
                label="Import File"
                className={classes.inputDiv}
                inputContainerClass={classes.input}
                value={formik.values.data_file}
                setValue={(val) => formik.setFieldValue("data_file", val)}
                errorTextClass={classes.errorText}
                errorText={
                  (formik.touched.data_file && formik.errors.data_file) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.data_file?.join(" "))
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={4}></Col>
            <Col md={8}>
              <div className="ml-10">
                {modeRadioOptions.map((option) => (
                  <Row key={option.value}>
                    <Col md={12}>
                      <label className="flex items-center mb-[10px] cursor-pointer">
                        <input
                          id={`mode-${option.value}`}
                          className="mr-[10px]"
                          // className="mr-[10px] accent-color: #007bff;"
                          type="radio"
                          value={option.value}
                          checked={formik.values.mode === option.value}
                          onChange={() =>
                            formik.setFieldValue("mode", option.value)
                          }
                        />
                        <span className="text-sm">{option.label}</span>
                        {/* <span className="text-sm color: #333;">{option.label}</span> */}
                      </label>
                    </Col>
                  </Row>
                ))}
              </div>
              {((formik.touched.mode && formik.errors.mode) ||
                saveError?.status === 422) && (
                <div className={classes.error}>
                  {formik?.errors?.mode ??
                    saveError?.response?.data?.errors?.mode?.join(" ")}
                </div>
              )}
            </Col>
          </Row>
          <div className="mt-4 gap-x-10 flex justify-center">
            <div className="w-1/3">
              <Button
                variant="outlined"
                label="Cancel"
                onClick={closeModal}
                type="button"
                className={classes.button}
                disabled={saveIsPending}
              />
            </div>
            <div className="w-1/3">
              <Button
                variant="green-gradient"
                label={saveIsPending ? <Spinner /> : "Upload"}
                type="submit"
                className={classes.button}
                disabled={saveIsPending}
              />
            </div>
          </div>
        </form>

        {failedRowsTableData.length > 0 && (
          <Container fluid className="py-4">
            {/* Failed Rows Section */}
            <h2 className="mb-3">Failed Rows</h2>
            <Row>
              <Col md={12}>
                <div className={classes.table}>
                  <ScrollContainer>
                    <Table
                      loading={saveIsPending}
                      tableData={failedRowsTableData}
                      headings={tableHeadings}
                      striped
                      hover
                    />
                  </ScrollContainer>
                </div>
              </Col>
            </Row>
          </Container>
        )}
      </ModalSkeleton>
    </div>
  );
}
